import { useMemo } from 'react';
import { createResourceAtom } from 'lib/atoms';
import { createMutationAtom } from 'atoms/create-resource-atom';
import type {
  LandingInterpretationIntervalsResponse,
  PreDrillWorkspace,
  WellLandingAnalysisInstancesResponse,
  WellLandingAnalysisPreview,
} from 'types/api-responses';

// INS-12936 — the Landing Analysis Interpretation section's reads and writes.

// The two reads are made per BORE (review): one shared atom carried bore A's runs, catalog or
// failure into bore B's sheet until B's answer landed, and a slow A answer could land over B's.
// Memoised by uwi14, as the run read below is by href, so each bore reads only its own answers.

// POST /queries/well_landing_analyses — the bore's prior runs, for the Landing Analysis picker.
export const useLandingAnalysisRunsAtom = (uwi14: string) =>
  useMemo(
    () =>
      createMutationAtom<
        WellLandingAnalysisInstancesResponse,
        'wellLandingAnalysisQuery'
      >(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a fresh atom per bore is the point
    [uwi14]
  );

// POST /queries/landing_interpretation_intervals — the basin column with nearby landed counts.
export const createLandingIntervalsAtom = () =>
  createMutationAtom<
    LandingInterpretationIntervalsResponse,
    'landingInterpretationIntervalsQuery'
  >();
export type LandingIntervalsAtom = ReturnType<
  typeof createLandingIntervalsAtom
>;

export const useLandingInterpretationIntervalsAtom = (uwi14: string) =>
  useMemo(createLandingIntervalsAtom, [uwi14]);

// POST …/landing_interpretation — Mark as Correct. Answers with the bore show.
export const markLandingCorrectAtom = createMutationAtom<
  PreDrillWorkspace,
  'markLandingInterpretationCorrect'
>();

// DELETE …/landing_interpretation — Remove Correct Status. Answers with the bore show.
export const removeLandingCorrectAtom = createMutationAtom<
  PreDrillWorkspace,
  'removeLandingInterpretation'
>();

// GET /well_landing_analyses/:id — the selected run's in-zone split and parameters.
export function useLandingAnalysisRunAtom(href: string | null | undefined) {
  return useMemo(
    () =>
      createResourceAtom<WellLandingAnalysisPreview>(
        href ?? undefined,
        'keep-old-data-on-refetch'
      ),
    [href]
  );
}
